import { createFileRoute, redirect } from "@tanstack/react-router";
import { DetailsPage } from "@majourneys/booking";
import { activitiesApi } from "../../../features/activities/api/activities.api";
import { activityKeys } from "../../../features/activities/constants/query-keys";
import { checkoutBlockReason, type CheckoutActivity } from "../../../features/activities/lib/checkout-guard";
import { transferHubRedirect } from "../../../features/transport/lib/hub-redirect";
import { withApi404 } from "../../../lib/route-loader";
import { useBookingSessionUser } from "../../../features/account/hooks/use-booking-session-user";

/** Fills a signed-in guest's name and email into the details form. */
function MajourneysDetailsPage() {
  return <DetailsPage sessionUser={useBookingSessionUser()} />;
}

export const Route = createFileRoute("/$lang/_layout/$slug/details")({
  loader: ({ params, context, location }) =>
    withApi404(async () => {
      const { queryClient } = context;
      const lang = params.lang;
      const response = await queryClient.ensureQueryData({
        queryKey: activityKeys.detail(params.slug, lang),
        queryFn: () => activitiesApi.getBySlug(params.slug, lang),
      });
      const a = response.data as CheckoutActivity & {
        slug?: string;
        template?: string | null;
        typePrice?: number;
        category?: { slug?: string | null } | null;
      };
      const hub = transferHubRedirect(a, params.lang);
      if (hub) throw redirect(hub);
      // A restaurant table is booked from the sheet on its page (free, no
      // packages, no totals) and an editorial place is not booked at all, so
      // an old or typed link here goes back to the page, choices kept. A flow
      // page, so a normal redirect, as on checkout.
      const blocked = checkoutBlockReason(a);
      if (blocked === "restaurant" || blocked === "explore") {
        throw redirect({
          to: "/$lang/$slug",
          params,
          search: (prev: Record<string, unknown>) => prev,
        });
      }
      const isStay = a.template === "stay" || a.typePrice === 2;
      const search = location.search as {
        date?: string;
        dateEnd?: string;
        checkOut?: string;
        time?: string;
        pkg?: string;
      };
      if (isStay) {
        const date = search.date;
        const dateEnd = search.dateEnd ?? search.checkOut;
        if (!date || !dateEnd) {
          throw redirect({
            to: "/$lang/$slug",
            params,
            search: (prev: Record<string, unknown>) => prev,
          });
        }
      } else {
        // Default flow: details requires date + time. pkg is optional — falls back
        // to a.packages[0] in the page.
        if (!search.date || !search.time) {
          throw redirect({
            to: "/$lang/$slug/packages",
            params,
            search: (prev: Record<string, unknown>) => prev,
          });
        }
      }
      return null;
    }),
  component: MajourneysDetailsPage,
});
